import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import * as _ from "lodash";
import { debounceTime, Subject, takeUntil } from "rxjs";

export class SearchableDropdownValueSelectedClass {
  controlName: string = "";
  selectedItem: any;
}

@Component({
  selector: "searchable-dropdown",
  templateUrl: "./searchable-dropdown.component.html",
  styleUrls: ["./searchable-dropdown.component.scss"],
})
export class SearchableDropdownComponent implements OnInit, OnChanges {
  @Input() controlName: string = "";
  @Input() placeholderLabel: string = "";
  @Input() noEntryLabel: string = "No items found";
  @Input() rootPlaceholderLabel: string = "";
  @Input() valueMapKey: string = "value";
  @Input() displayKey: string = "";
  @Input() itemList: any[] = [];
  @Input() required: boolean = true;
  @Input() isDisplayError: boolean = true;
  @Input() mainControl: FormControl = new FormControl();
  @Input() readOnly: boolean = false;
  @Input() searchableDropdown: boolean = false;
  @Input() handleEnterPress?: (item: any, controlName: string) => void;
  @Input() searchFn?: (item: string) => Promise<any[]>;
  @Input() scrollToBottomFn?: () => Promise<any[]>;
  @Input() disableSorting = false;
  @Input() multiple = false;
  @Input() clearable = false;
  @Input() isStyled: boolean = false;

  @Output() valueSelected =
    new EventEmitter<SearchableDropdownValueSelectedClass>();
  @Output() onSearch = new EventEmitter();

  @ViewChild("select") select: any;
  public currentSearchTerm: string = "";
  private searchSubject = new Subject<any>();
  private destroy$ = new Subject<void>();
  public disabled: boolean = false;
  public searchable = false;
  private isDataLoading = false;

  constructor() {}

  ngOnInit() {
    this.searchSubject
      .pipe(debounceTime(300), takeUntil(this.destroy$)) // 300ms debounce
      .subscribe((searchTerm) => this.performSearch(searchTerm));
  }

  ngOnChanges() {
    this.disabled = this.readOnly || this.itemList?.length === 0;

    this.searchable = this.searchableDropdown || this.itemList?.length > 5;

    if (!this.disableSorting && this.itemList?.length > 2) {
      this.itemList = this.sortArray(this.itemList, this.displayKey);
    }

    if (this.searchableDropdown) {
      this.disabled = false;
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key !== "Enter" || !this.multiple || !this.handleEnterPress) {
      return;
    }

    const inputValue = (event.target as HTMLInputElement).value.trim();
    if (!inputValue) return;

    const items = inputValue
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const currentValues = this.mainControl.value || [];
    const updatedValues = [...currentValues];
    const updatedItemList = [...this.itemList];

    const alreadySelectedValues = new Set(currentValues);
    const alreadySelectedLabels = new Set(
      updatedItemList
        .filter((o) => alreadySelectedValues.has(o.value))
        .map((o) => o.label.toLowerCase())
    );

    const seenInInput = new Set<string>();

    for (const item of items) {
      const labelLower = item.trim().toLowerCase();

      if (seenInInput.has(labelLower)) continue;
      seenInInput.add(labelLower);

      if (alreadySelectedLabels.has(labelLower)) continue;

      const existingOption = updatedItemList.find((opt) => {
        const optLabel = (opt.label || "").trim().toLowerCase();
        return optLabel === labelLower;
      });

      if (existingOption) {
        updatedValues.push(existingOption.value);
      } else {
        const newOption = {
          value: Date.now() + Math.random(),
          label: item.trim(),
          isNew: true,
        };

        this.handleEnterPress?.(newOption, this.controlName);

        updatedItemList.push(newOption);
        updatedValues.push(newOption.value);
      }
    }

    this.itemList = updatedItemList;
    this.mainControl.setValue(updatedValues);
    this.select.searchTerm = "";
  }

  sortArray(itemlist: any[], sortKey: string): any[] {
    if (!itemlist.length) return itemlist;
    return _.sortBy(itemlist, [(o) => o[sortKey]?.toLowerCase()]);
  }

  onValueChange() {
    const value = this.mainControl.value;
    const selectedItem = this.multiple
      ? this.itemList.filter((elm) => value.includes(elm[this.valueMapKey]))
      : this.itemList.find((elm) => elm[this.valueMapKey] === value);

    this.valueSelected.emit({ controlName: this.controlName, selectedItem });
  }

  // async onSearchText(event: any) {
  //   const data = (await this.searchFn?.(event.term)) || [];
  //   if (data.length) {
  //     this.itemList = [...data];
  //   }
  // }
  onSearchText(event: any) {
    this.currentSearchTerm = event.term?.trim();
    this.searchSubject.next(event);
  }

  async performSearch(event: any) {
    const data = (await this.searchFn?.(event.term)) || [];
    if (data.length) {
      this.itemList = [...data];
    }
  }

  isRequired() {
    return (
      this.required &&
      this.mainControl.touched &&
      !this.mainControl.value &&
      this.isDisplayError
    );
  }

  async loadMoreData() {
    if (this.isDataLoading) return;
    this.isDataLoading = true;

    const data = await this.scrollToBottomFn?.();

    this.isDataLoading = false;
    if (data?.length) {
      this.itemList = [...data];
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
